import { Dispatch, MouseEvent, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import {
  fetchStructuresUnitsAsTree,
  IStructuresUnitsFilters,
} from 'APIPackage/RequirementsNpaModule/services/requirementsNpaApi';
import {
  getIsMandatoryCreated,
  getIsSuCreated,
} from 'KnmPackage/RequirementsNpaModalModule/selectors/requirementsNpaSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  setIsMandatoryCreatedAction,
  setIsSuCreatedAction,
} from 'KnmPackage/RequirementsNpaModalModule/actions/requirementsNpaActions';
import {
  IRequirementsMandatoryNew,
  IStructuresUnit,
} from 'APIPackage/RequirementsNpaModule/interfaces/IStructuresUnitsAsTreeResponse';

import styles from './NpaItem.module.scss';
import { StructuresUnitItem } from '../StructuresUnitItem';
import { InputFormRequirementsNpa, ISelectedStructuralUnit } from '../interface';
import { Loader } from '../Loader';
import { RequirementsNpaSourceEnum } from '../../RequirementsNpaModalModule/enums/RequirementsNpaSourceEnum';
import { IKnmErknmFormRequirementNpa } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRequirementNpa';
import { IKnmPmFormRequirementsNpa } from '../../../KnmPmPackage/MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { convertFormDateValueToKnmDataDate } from '../../MainModule/common/convertFormDateValueToKnmDataDate';
import { getKnmDataCreated } from '../../MainModule/selectors/knmSelectors';

function filterNodesWithData(nodes: IStructuresUnit[]): IStructuresUnit[] {
  return nodes
    .map(node => {
      // Рекурсивно фильтруем детей
      const filteredChildren = filterNodesWithData(node.children);

      // Проверяем, есть ли данные в текущем узле или в отфильтрованных детях
      const hasDataInCurrentNode = (node.data && node.data.length > 0) || (node.dataSize && node.dataSize > 0);
      const hasDataInChildren = filteredChildren.length > 0;

      // Если есть данные в текущем узле или его детях, возвращаем узел с отфильтрованными детьми
      if (hasDataInCurrentNode || hasDataInChildren) {
        return {
          ...node,
          children: filteredChildren,
        };
      }

      // Иначе возвращаем null, чтобы отфильтровать этот узел
      return null;
    })
    .filter((node): node is IStructuresUnit => node !== null);
}

interface INpaItemProps {
  addMandatory: (path: string, suId: number) => void;
  canEditNpa: boolean;
  closeNpa: (id: number) => void;
  didCloseInnerModal: boolean;
  ignoredNpaIds?: number[];
  inputRequirementsNpa?: InputFormRequirementsNpa[];
  isOpen: boolean;
  isSusOpen: {
    [key: string]: boolean;
  };
  item: IRequirementNpa;
  knoVersionId: string;
  mrIds?: number[];
  onClickAddNewSU: () => void;
  onOpenNpaForEdit: (e: MouseEvent<HTMLButtonElement>) => void;
  onUpdateStructures: (item: IRequirementNpa) => void;
  requirementsErknmDisabled?: boolean;
  setIsOpen: (val: boolean) => void;
  setIsSuOpen: (path: string, val: boolean) => void;
  setSuIdsSelected: Dispatch<SetStateAction<ISelectedStructuralUnit[]>>;
  structuresUnitsFilters?: IStructuresUnitsFilters;
  suIdsSelected: ISelectedStructuralUnit[];
  supervisionVersionId: string;
  uniqStructureId?: string;
  updateNpaList: (npaId: number, suId: string, list: IRequirementsMandatoryNew[]) => void;
  usedNpa: IKnmErknmFormRequirementNpa[] | IKnmPmFormRequirementsNpa[] | undefined;
  withRequirements: boolean;
}

interface INpaItemStoreProps {
  created: string | null | undefined;
  isMandatoryCreated: boolean;
  isSuCreated: boolean;
}

const NpaItemWithoutStore = ({
  addMandatory,
  canEditNpa,
  closeNpa,
  didCloseInnerModal,
  ignoredNpaIds,
  inputRequirementsNpa,
  isOpen,
  isSusOpen,
  item,
  knoVersionId,
  mrIds: mrIdsProp,
  onClickAddNewSU,
  onOpenNpaForEdit,
  onUpdateStructures,
  requirementsErknmDisabled,
  setIsOpen,
  setIsSuOpen,
  setSuIdsSelected,
  structuresUnitsFilters,
  suIdsSelected,
  supervisionVersionId,
  uniqStructureId,
  updateNpaList,
  usedNpa,
  withRequirements,
  created,
  isMandatoryCreated,
  isSuCreated,
}: INpaItemProps & INpaItemStoreProps) => {
  const [isDefaultUnitsOpen, setIsDefaultUnitsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmptyMandatory, setIsEmptyMandatory] = useState(false);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const loadStructuresUnits = useCallback(async () => {
    setIsLoading(true);

    let suIds: number[] = [];
    const mrIds: number[] = [];

    if (inputRequirementsNpa) {
      suIds = inputRequirementsNpa.map(e => e.structuralUnit.id);
      inputRequirementsNpa.forEach(e => {
        e?.mandatory?.forEach(m => {
          m.id && mrIds.push(m.id);
        });
      });
    }

    const units = await fetchStructuresUnitsAsTree({
      npaId: item.id,
      knmCreationDate: convertFormDateValueToKnmDataDate(created) || undefined,
      knoVersionId,
      supervisionVersionId,
      withRequirements,
      suId: uniqStructureId,
      suIds,
      mrIds,
      ...structuresUnitsFilters,
    });

    if (units?.nodes?.length <= 1) {
      setIsDefaultUnitsOpen(true);
      if (units.nodes.length === 1) {
        setIsSuOpen(`${item.id}/${units.nodes[0].levelTitle}: ${units.nodes[0].value}`, true);
      }
    }

    onUpdateStructures({ ...item, structures: units });
    setIsLoading(false);
  }, [
    item,
    knoVersionId,
    supervisionVersionId,
    withRequirements,
    onUpdateStructures,
    uniqStructureId,
    structuresUnitsFilters,
  ]);

  const onClickRowHandler = useCallback(() => {
    setIsOpen(!isOpen);
    if (!isOpen && !item.structures) {
      loadStructuresUnits();
    }
    if (!isOpen) {
      closeNpa(item.id);
    }
  }, [isOpen, setIsOpen, closeNpa]);

  // При монтировании NpaItem: если строка НПА уже раскрыта — сразу загружаем дерево структурных единиц.
  useEffect(() => {
    if (isOpen) {
      loadStructuresUnits();
    }
  }, []);

  // Перезагрузка дерева после создания новой СЕ или новой формулировки во вложенной модалке.
  useEffect(() => {
    if (isSuCreated && isOpen) {
      loadStructuresUnits();
      dispatch(setIsSuCreatedAction(false));
    }
    if (isMandatoryCreated && isOpen) {
      loadStructuresUnits();
      dispatch(setIsMandatoryCreatedAction(false));
    }
  }, [isSuCreated, isMandatoryCreated, isOpen]);

  // Отображение чекбокса выбора НПА, при условии выбранной хотя бы одной формулировки (еще не добавленной в паспорт)
  const isCheckedNpa = useMemo(() => {
    return !!suIdsSelected.find(i => i.npaId === item.id && i.mandatoryId && !ignoredNpaIds?.includes(i.mandatoryId));
  }, [suIdsSelected, item, ignoredNpaIds]);

  // Запрет на добавление структурных единиц и формулировок
  const isCanNotEdit = useMemo(() => {
    return item.structures?.suCreateDisabled === true && item.structures?.requirementsErknmDisabled === true;
  }, [item.structures]);

  const nodes = useMemo(() => {
    if (!item.structures?.nodes) return [];
    // Если запрещено редактировать структурные единицы, то скрываем пустые элементы
    if (isCanNotEdit) {
      // const filteredNodes = filterNodesByDataSize(item.structures.nodes);
      const filteredNodes = filterNodesWithData(item.structures.nodes);

      if (!filteredNodes.length && item.structures.nodes.length > 0) {
        // Если вообще нет формулировок, то выведем сообщение
        setIsEmptyMandatory(true);
      } else {
        setIsEmptyMandatory(false);
      }

      return filteredNodes;
    }

    return item.structures?.nodes || [];
  }, [item.structures?.nodes, isCanNotEdit]);

  // После загрузки дерева СЕ — восстанавливаем в suIdsSelected формулировки,
  // которые уже выбраны в паспорте КНМ (usedNpa), только для текущего НПА (item.id).
  // Обновление через prev => …, чтобы не затереть выбор, добавленный параллельно в других NpaItem / StructuresUnitItem.
  useEffect(() => {
    if (usedNpa?.length && nodes.length) {
      setSuIdsSelected(prev => {
        const newList = [...prev];
        let isChanged = false;

        usedNpa.forEach(preSelectedNpa => {
          if (preSelectedNpa.npa?.id === item.id) {
            const node = nodes.find(n => n.id === preSelectedNpa.structuralUnit.id.toString());

            if (node) {
              preSelectedNpa.mandatory?.forEach(preSelectedMandatory => {
                const mandatoryId =
                  'id' in preSelectedMandatory ? preSelectedMandatory?.id : preSelectedMandatory?.mandatory?.id;

                if (mandatoryId && !newList.some(elem => elem.mandatoryId === mandatoryId)) {
                  newList.push({
                    id: node.id || '',
                    path: `${node.levelTitle}: ${node.value}`,
                    npaId: item.id,
                    mandatoryId,
                  });
                  isChanged = true;
                }
              });
            }
          }
        });

        return isChanged ? newList : prev;
      });
    }
  }, [item, nodes, usedNpa]);

  // Структурная единица для НПА может быть добавлена если НПА не из РОТ
  const isSuCreateDisabled = useMemo(() => {
    if (item?.structures && 'suCreateDisabled' in item.structures) {
      return item.structures.suCreateDisabled;
    }

    return item?.source === RequirementsNpaSourceEnum.ROT;
  }, [item]);

  return (
    <div className={styles.Wrapper} id={item.id.toString()}>
      <div className={styles.NpaRow} onClick={onClickRowHandler}>
        <div className={styles.Checkbox} style={{ width: '44px' }}>
          <Checkbox id={item.id.toString()} theme="system" checked={isCheckedNpa} />
        </div>
        <div style={{ width: 'calc(14% - 44px)' }}>
          <div className={styles.NumberAndDate}>
            <span>{item.number}</span>
            <span>
              {formatDate({
                date: item.acceptanceDate || '',
                dateFormat: DATE_FORMAT_LONG_FNS,
                parseFormat: SYSTEM_DATE_FORMAT_FNS,
              })}
            </span>
          </div>
        </div>
        <div className={styles.Title}>{item.title}</div>
        <div className={styles.IconWrapper}>
          {!uniqStructureId && !inputRequirementsNpa && canEditNpa && (
            <Button text="" variant="link" startIcon={styles.EditIcon} onClick={onOpenNpaForEdit} type="button" />
          )}
          <IconCollapse className={`${styles.IconCollapse} ${isOpen && styles.IconCollapseOpen}`} />
        </div>
      </div>
      {!uniqStructureId && isOpen && !inputRequirementsNpa && !isSuCreateDisabled && !requirementsErknmDisabled && (
        <div className={styles.ButtonCreateUnitWrapper}>
          <Button
            text="Добавить структурную единицу"
            variant="link"
            className={styles.CreateUnitButton}
            startIcon={styles.AddIcon}
            onClick={onClickAddNewSU}
            type="button"
          />
        </div>
      )}
      {isOpen && nodes.length > 0 && (
        <div className={styles.StructuresList}>
          {nodes.map((node, i) => {
            return (
              <StructuresUnitItem
                key={i}
                _key={i.toString()}
                item={node}
                paddingPx={0}
                npaId={item.id}
                path={`${node.levelTitle}: ${node.value}`}
                isDefaultOpen={isDefaultUnitsOpen}
                withRequirements={withRequirements}
                suIdsSelected={suIdsSelected}
                setSuIdsSelected={setSuIdsSelected}
                addMandatory={addMandatory}
                setIsSuOpen={setIsSuOpen}
                isSusOpen={isSusOpen}
                didCloseInnerModal={didCloseInnerModal}
                knoVersionId={knoVersionId}
                supervisionVersionId={supervisionVersionId}
                created={created}
                updateNpaList={updateNpaList}
                inputRequirementsNpa={inputRequirementsNpa}
                structuresUnitsFilters={structuresUnitsFilters}
                requirementsErknmDisabled={item?.structures?.requirementsErknmDisabled || requirementsErknmDisabled}
                ignoredNpaIds={ignoredNpaIds}
                mrIds={mrIdsProp}
                usedNpa={usedNpa}
              />
            );
          })}
        </div>
      )}
      {isOpen && item && nodes?.length === 0 && !isLoading && (
        <div className={isEmptyMandatory ? styles.EmptySearchWarning : styles.EmptySearch}>
          <span className={isEmptyMandatory ? styles.InfoWarningIcon : styles.InfoIcon}></span>
          <span className={isEmptyMandatory ? styles.InformationSupportTextWarning : styles.InformationSupportText}>
            {isEmptyMandatory ? 'Нет ни одной доступной формулировки' : 'Структурные единицы НПА не найдены'}
          </span>
        </div>
      )}
      {isOpen && isLoading && <Loader />}
    </div>
  );
};

const mapStateToProps = (state: IRootState): INpaItemStoreProps => ({
  created: getKnmDataCreated(state),
  isSuCreated: getIsSuCreated(state),
  isMandatoryCreated: getIsMandatoryCreated(state),
});

export const NpaItem = connect(mapStateToProps)(NpaItemWithoutStore);
